import React from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ComplianceItem } from '@/lib/dealStages'

const STATE_LABEL: Record<ComplianceItem['state'], { text: string; className: string }> = {
  auto: { text: 'Auto', className: 'text-muted-foreground' },
  done: { text: 'Done', className: 'text-[#4D7C0F]' },
  waiting: { text: 'Waits', className: 'text-muted-foreground' },
  needs_you: { text: 'Needs you', className: 'text-[#B45309]' },
}

/**
 * "Athios handles": the paperwork on a deal and where each piece stands. Items that need the
 * athlete are called out; everything else is automatic.
 */
const ComplianceCard: React.FC<{
  items: ComplianceItem[]
  onAction?: (id: string) => void
  className?: string
}> = ({ items, onAction, className }) => (
  <div className={cn('rounded-2xl border border-border bg-card p-4 shadow-sm', className)}>
    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
      Athios handles
    </p>
    <ul className="mt-2 divide-y divide-border">
      {items.map((item) => {
        const needsYou = item.state === 'needs_you'
        const Row: any = needsYou && onAction ? 'button' : 'div'
        return (
          <li key={item.id}>
            <Row
              type={needsYou && onAction ? 'button' : undefined}
              onClick={needsYou && onAction ? () => onAction(item.id) : undefined}
              className={cn(
                'flex w-full items-start justify-between gap-3 py-2 text-left text-sm',
                needsYou && onAction && 'hover:underline',
              )}
            >
              <span className="flex min-w-0 flex-1 items-start gap-2">
                {item.state === 'done' ? (
                  <Check className="h-3.5 w-3.5 shrink-0 text-[#4D7C0F]" strokeWidth={3} />
                ) : (
                  <span
                    className={cn(
                      'h-1.5 w-1.5 shrink-0 rounded-full',
                      needsYou ? 'bg-[#F59E0B]' : 'bg-border',
                    )}
                  />
                )}
                <span className={cn('min-w-0', needsYou ? 'text-foreground' : 'text-foreground')}>
                  {item.label}
                </span>
              </span>
              <span
                className={cn(
                  'max-w-[45%] shrink-0 text-right text-xs font-semibold',
                  STATE_LABEL[item.state].className,
                )}
              >
                {item.detail ?? STATE_LABEL[item.state].text}
              </span>
            </Row>
          </li>
        )
      })}
    </ul>
  </div>
)

export default ComplianceCard
